<template>
  <slide-in-modal
    ref="modal"
    :class="className"
    :close-label="commonTexts.close"
    :disabled="false"
    :modal-header-desc="headerDesc"
    :modal-heading="heading"
    :modal-heading-icon-name="headingIcon"
    :modal-open="modalIsOpen"
    :toggle-modal="toggleModal"
  >
    <transition mode="out-in" name="modal-content">
      <!-- The form keeps its chosen files while the visitor looks at the preview. -->
      <keep-alive :key="greetingCount" :include="['CreateCommentForm']">
        <component
          :is="modalContent.component"
          v-bind="modalContent.properties"
        />
      </keep-alive>
    </transition>
  </slide-in-modal>
</template>

<script>
  import SlideInModal from "Components/SlideInModal.vue";
  import Helpers from "Modules/Helpers";
  import { sprintf } from "sprintf-js";
  import { defineAsyncComponent } from "vue";
  import { mapActions, mapMutations, mapState } from "vuex";
  import {
    DONATION,
    DONATION_INITIAL,
    DONATION_QUERY,
    FORM,
    FORM_INITIAL,
    FORM_QUERY,
    GIFT_DONE,
    MEDIA_INITIAL,
    PREVIEW,
  } from "../../createCommentStepsHelper";
  import CreateCommentDonation from "./CreateCommentDonation.vue";
  import CreateCommentGiftDone from "./CreateCommentGiftDone.vue";
  import CreateCommentPreview from "./CreateCommentPreview.vue";
  import CreateCommentQuery from "./CreateCommentQuery.vue";
  import CreateCommentSkeleton from "./CreateCommentSkeleton.vue";

  export default {
    components: {
      CreateCommentDonation,
      // Deferred so the form and its uploader (~85 kB minified) are fetched
      // when the form step renders, not on every obituary page load.
      CreateCommentForm: defineAsyncComponent({
        loader: () => import("./CreateCommentForm.vue"),
        loadingComponent: CreateCommentSkeleton,
      }),
      CreateCommentGiftDone,
      CreateCommentPreview,
      CreateCommentQuery,
      CreateCommentSkeleton,
      SlideInModal,
    },

    computed: {
      ...mapState("comments", ["files"]),
      ...mapState("createComment", [
        "modalContext",
        "prevContext",
        "modalIsOpen",
      ]),
      ...mapState("donations", ["donation", "isDonating"]),

      className() {
        return this.checkContext([FORM_QUERY, GIFT_DONE]) && this.donation
          ? "has-donated"
          : "";
      },
      hasFiles() {
        return !!this.files?.length;
      },

      headerDesc() {
        const { texts } = this;

        if (this.checkContext([GIFT_DONE])) {
          return "";
        }

        if (this.checkContext([PREVIEW])) {
          return this.hasFiles ? texts.previewLead : texts.previewLeadNoMedia;
        }

        if (this.checkContext([DONATION_QUERY]) && this.donation) {
          return texts.alreadyDonated;
        }

        if (this.checkContext([DONATION_QUERY, DONATION_INITIAL])) {
          return sprintf(
            texts.donateSubheading,
            this.orderInfo.deceasedNickname
          );
        }

        const hasMemorialFund = !!this.orderInfo?.memorialFund?.name;

        if (this.checkContext([DONATION])) {
          if (hasMemorialFund) {
            return sprintf(
              texts.donationThankYouDescWithFund,
              `<strong>${this.orderInfo?.memorialFund?.name}</strong>`
            );
          }

          return sprintf(texts.donationThankYouDesc);
        }

        if (this.checkContext([FORM_QUERY]) && this.donation) {
          return "";
        }

        if (this.checkContext([FORM_INITIAL, MEDIA_INITIAL])) {
          return sprintf(
            texts.writeCondolenceDesc,
            this.orderInfo.deceasedNickname
          );
        }

        return sprintf(texts.generalDesc, this.orderInfo.deceasedNickname);
      },

      heading() {
        const { texts } = this;

        if (this.checkContext([PREVIEW])) {
          return texts.previewHeading;
        }

        if (this.checkContext([DONATION_QUERY]) && this.donation) {
          return texts.weHaveReceievedYourDonation;
        }

        if (this.checkContext([DONATION_QUERY])) {
          return texts.doYouWantToDonate;
        }

        if (this.checkContext([DONATION, DONATION_INITIAL])) {
          return `${this.commonTexts.inMemoryOf} ${this.orderInfo.deceasedNickname}`;
        }

        if (this.checkContext([FORM_QUERY, GIFT_DONE]) && this.donation) {
          return texts.donationThankYou;
        }

        if (this.donation) {
          return texts.addCondolenceToDonation;
        }

        return texts.writeCondolence;
      },

      headingIcon() {
        if (this.checkContext([DONATION, DONATION_INITIAL, DONATION_QUERY])) {
          return "gift";
        }

        if (this.checkContext([FORM_QUERY, GIFT_DONE]) && this.donation) {
          return "check";
        }

        return "pen";
      },

      isInitial() {
        return this.checkContext([
          FORM_INITIAL,
          MEDIA_INITIAL,
          DONATION_INITIAL,
        ]);
      },

      modalContent() {
        if (
          this.checkContext([FORM, FORM_INITIAL, FORM_QUERY, MEDIA_INITIAL])
        ) {
          if (this.checkContext([FORM_INITIAL, MEDIA_INITIAL])) {
            Helpers.obituaryGreetingsLog(
              "GreetingInitial",
              this.orderInfo.obituaryId
            );
          } else if (this.checkContext([FORM_QUERY])) {
            Helpers.obituaryGreetingsLog(
              "GreetingQuery",
              this.orderInfo.obituaryId
            );
          } else {
            Helpers.obituaryGreetingsLog(
              "GreetingAfterDonation",
              this.orderInfo.obituaryId
            );
          }

          return {
            component: "create-comment-form",
            properties: {
              orderInfo: this.orderInfo,
            },
          };
        }

        if (this.checkContext([GIFT_DONE])) {
          return { component: "create-comment-gift-done", properties: {} };
        }

        if (this.checkContext([PREVIEW])) {
          return {
            component: "create-comment-preview",
            properties: {
              orderInfo: this.orderInfo,
            },
          };
        }

        if (this.checkContext([DONATION_QUERY])) {
          Helpers.obituaryGreetingsLog(
            "DonationQuery",
            this.orderInfo.obituaryId
          );

          return {
            component: "create-comment-query",
            properties: {
              orderInfo: this.orderInfo,
            },
          };
        }

        if (this.checkContext([DONATION, DONATION_INITIAL])) {
          if (this.checkContext([DONATION_INITIAL])) {
            Helpers.gtmObituaryEvent("donationInitial");
            Helpers.obituaryGreetingsLog(
              "DonationInitial",
              this.orderInfo.obituaryId
            );
          } else {
            Helpers.gtmObituaryEvent("donationStartedAfterGreeting");
            Helpers.obituaryGreetingsLog(
              "DonationStartedAfterGreeting",
              this.orderInfo.obituaryId
            );
          }

          return {
            component: "create-comment-donation",
            properties: {
              orderInfo: this.orderInfo,
            },
          };
        }

        return {
          component: "create-comment-skeleton",
          properties: null,
        };
      },
    },

    data() {
      return {
        commonTexts: lavendlaObituary.common,
        DONATION,
        DONATION_INITIAL,
        DONATION_QUERY,
        FORM,
        FORM_INITIAL,
        FORM_QUERY,
        greetingCount: 0,
        orderInfo: {
          currency: this.$mountEl.dataset.currency,
          deceasedNickname: this.$mountEl.dataset.deceasedNickname,
          fee: this.$mountEl.dataset.fee,
          memorialFund: JSON.parse(this.$mountEl.dataset.memorialFund),
          obituaryId: this.$mountEl.dataset.obituaryId,
          orderId: this.$mountEl.dataset.orderId,
          possessiveName: this.$mountEl.dataset.possessiveName,
        },
        texts: lavendlaObituary.createComment,
      };
    },

    methods: {
      ...mapMutations("donations", [
        "setHasDonated",
        "setIsDonating",
        "setIsPolling",
      ]),
      ...mapActions("createComment", [
        "changeModalContext",
        "removePrevContext",
        "toggleModal",
      ]),

      backToggleFunction() {
        if (!this.prevContext.length) {
          return;
        }

        const context = this.prevContext.at(-1);
        this.removePrevContext();
        this.changeModalContext(context);
      },

      checkContext(array) {
        return array.includes(this.modalContext);
      },

      getModalBoxWrapperHeight() {
        // compensating for padding, 80px, at the bottom of the modal + go back link.
        return this.$refs.modal.$refs.modalContent.clientHeight > 370
          ? this.$refs.modal.$refs.modalContent.clientHeight - 100
          : 370;
      },
    },
    name: "CreateCommentModal",

    provide() {
      return {
        timelineEnabled: this.$mountEl.dataset.timelineEnabled === "true",
      };
    },

    watch: {
      modalIsOpen() {
        // Closing drops the kept form, so the next greeting starts empty.
        this.greetingCount += 1;
      },
    },
  };
</script>
